import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { MoreVertical, Edit2, Trash2, Plus, ArrowRight, Info, FileText, Check, X, Clock, Hash, RotateCcw, ArrowLeftRight } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import CardItem from "./CardItem";
import ConfirmationModal from "./ConfirmationModal";
import SimpleQuillEditor from "./SimpleQuillEditor";
import { isEmptyHtml } from "../utils/htmlUtils";

// Incremental rendering: only mount the first N cards of a column and grow as
// the user scrolls. Keeps huge columns (e.g. an Archive with hundreds of cards)
// from mounting every tile at once.
const INITIAL_VISIBLE = 12;
const VISIBLE_STEP = 20;

// Defined at module scope (NOT inside ListColumn) so its component identity is
// stable across ListColumn re-renders. Declaring it inside the parent would
// give it a new identity every render, forcing React to remount its entire
// subtree — which reset the column's scroll position and reloaded card images.
const ColumnDroppable = ({ status, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: {
      type: "column",
      droppableType: "column",
      status: status,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-h-0 flex flex-col transition-all duration-200 ${
        isOver
          ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-400 border-dashed rounded-lg"
          : ""
      }`}
    >
      {children}
    </div>
  );
};

const ListColumn = React.memo(
  ({
    title,
    description,
    status,
    cards,
    color,
    bgColor,
    borderColor,
    textColor,
    onCardUpdated,
    onCardDeleted,
    onCardRestored,
    onStatusChange,
    onCardClick,
    activeTimer,
    activeTimers,
    onTimerStart,
    onTimerStop,
    projectId,
    onColumnUpdate,
    onColumnDelete,
    onAddCard,
    onMoveAllCards,
  }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDescriptionTooltip, setShowDescriptionTooltip] = useState(false);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState(description || "");
    const [newTitle, setNewTitle] = useState(title);
    const [selectedColor, setSelectedColor] = useState(color || "gray");
    const storageKey = `pms_col_sort_${projectId || "default"}_${status}`;
    const [sortBy, setSortBy] = useState(() => {
      try {
        const saved = localStorage.getItem(`pms_col_sort_${projectId || "default"}_${status}`);
        return saved === "recentlyMoved" || saved === "recentlyCreated" || saved === "cardNumber" ? saved : null;
      } catch {
        return null;
      }
    });

    // Sync sortBy if projectId or column status changes
    useEffect(() => {
      try {
        const saved = localStorage.getItem(`pms_col_sort_${projectId || "default"}_${status}`);
        setSortBy(saved === "recentlyMoved" || saved === "recentlyCreated" || saved === "cardNumber" ? saved : null);
      } catch {
        setSortBy(null);
      }
    }, [projectId, status]);
    const menuRef = useRef(null);
    const descriptionIconRef = useRef(null);
    const descriptionModalRef = useRef(null);
    const cardsScrollRef = useRef(null);
    const cardsScrollTopRef = useRef(0);
    const columnRootRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
    // Lazy-mount this column's cards only once it scrolls near the viewport, so
    // off-screen columns don't mount hundreds of card nodes on first paint.
    const [hasBeenVisible, setHasBeenVisible] = useState(false);

    useEffect(() => {
      if (hasBeenVisible) return;
      const el = columnRootRef.current;
      if (!el) return;
      if (typeof IntersectionObserver === "undefined") {
        setHasBeenVisible(true);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setHasBeenVisible(true);
            observer.disconnect();
          }
        },
        { root: null, rootMargin: "200px 1200px" }
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, [hasBeenVisible]);

    // Grow the visible window when cards are added so a newly created card
    // isn't hidden behind the incremental limit.
    const prevCardCountRef = useRef(cards.length);
    useEffect(() => {
      if (cards.length > prevCardCountRef.current) {
        const added = cards.length - prevCardCountRef.current;
        setVisibleCount((c) => c + added);
      }
      prevCardCountRef.current = cards.length;
    }, [cards.length]);

    // Sort cards independently for this column based on active filter:
    // 1. recentlyMoved: newest to oldest by move date/time
    // 2. recentlyCreated: newest to oldest by creation date
    // 3. cardNumber: lowest to highest by card number (ascending)
    const sortedCards = useMemo(() => {
      if (!cards || cards.length === 0) return [];
      if (!sortBy) return cards;

      const getCardTime = (card) => {
        if (card?.createdAt) {
          const t = new Date(card.createdAt).getTime();
          if (!isNaN(t)) return t;
        }
        if (card?._id && typeof card._id === "string" && card._id.length === 24) {
          const t = parseInt(card._id.substring(0, 8), 16) * 1000;
          if (!isNaN(t)) return t;
        }
        return 0;
      };

      const getMoveTime = (card) => {
        if (card?.movedAt) {
          const t = new Date(card.movedAt).getTime();
          if (!isNaN(t)) return t;
        }
        return getCardTime(card);
      };

      const getCardNumber = (card) => Number(card?.cardNumber) || 0;

      return [...cards].sort((a, b) => {
        if (sortBy === "recentlyMoved") {
          const diff = getMoveTime(b) - getMoveTime(a);
          return diff !== 0 ? diff : getCardNumber(a) - getCardNumber(b);
        }
        if (sortBy === "recentlyCreated") {
          const diff = getCardTime(b) - getCardTime(a);
          return diff !== 0 ? diff : getCardNumber(b) - getCardNumber(a);
        }
        if (sortBy === "cardNumber") {
          const diff = getCardNumber(a) - getCardNumber(b);
          return diff !== 0 ? diff : getCardTime(b) - getCardTime(a);
        }
        return 0;
      });
    }, [cards, sortBy]);

    // Cards currently mounted (grows on scroll). Slicing a changed `sortedCards`
    // array is cheap and keeps drag/reorder behavior intact for visible cards.
    const visibleCards =
      sortedCards.length > visibleCount ? sortedCards.slice(0, visibleCount) : sortedCards;

    // Sync editedDescription when description prop changes
    useEffect(() => {
      setEditedDescription(description || "");
    }, [description]);

    // Keep the user's live scroll position when the cards array reference
    // changes (socket/filter/drag churn). We read the DOM's current scrollTop
    // and only clamp it down if content shrank — never force it to a saved
    // value, which previously fought the user's scroll and snapped to the top.
    useLayoutEffect(() => {
      const cardsList = cardsScrollRef.current;
      if (!cardsList) return;
      const maxScrollTop = Math.max(
        0,
        cardsList.scrollHeight - cardsList.clientHeight
      );
      if (cardsList.scrollTop > maxScrollTop) {
        cardsList.scrollTop = maxScrollTop;
      }
    }, [sortedCards]);
    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setShowMenu(false);
        }
        if (
          descriptionModalRef.current &&
          !descriptionModalRef.current.contains(event.target)
        ) {
          // Check if we're clicking inside a quill tooltip or picker
          if (!event.target.closest(".ql-tooltip") && !event.target.closest(".ql-picker-options")) {
            setIsEditingDescription(false);
          }
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    const handleRename = () => {
      if ((newTitle.trim() && newTitle !== title) || selectedColor !== color) {
        onColumnUpdate(status, { 
          name: newTitle.trim(),
          color: selectedColor 
        });
      }
      setIsEditing(false);
    };

    const handleKeyPress = (e) => {
      if (e.key === "Enter") {
        handleRename();
      } else if (e.key === "Escape") {
        setNewTitle(title);
        setSelectedColor(color || "gray");
        setIsEditing(false);
      }
    };

    const handleDelete = () => {
      setShowDeleteConfirm(true);
      setShowMenu(false);
    };

    const confirmDelete = () => {
      onColumnDelete(status);
      setShowDeleteConfirm(false);
    };

    const handleSort = (type) => {
      setSortBy((prev) => {
        const next = prev === type ? null : type;
        try {
          if (next) {
            localStorage.setItem(storageKey, next);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (e) {
          console.error("Failed to persist column sort:", e);
        }
        return next;
      });
      if (cardsScrollRef.current) {
        cardsScrollRef.current.scrollTop = 0;
      }
      setShowMenu(false);
    };

    const handleUpdateDescription = () => {
      onColumnUpdate(status, { description: editedDescription });
      setIsEditingDescription(false);
    };

    // Use passed props for colors, or fallback to generated ones if missing (though they should be passed)
    // We prioritize the passed props because they contain dark mode variants
    const finalTextColor = textColor || `text-${color}-700`;
    const finalBgColor = bgColor || `bg-white dark:bg-slate-800`;
    const finalBorderColor = borderColor || `border-gray-200 dark:border-slate-700`;

    return (
      <div
        ref={columnRootRef}
        className={`bg-[#f1f2f4] dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700 h-full flex flex-col transition-all duration-200 ${
          isHovered ? "shadow-lg" : "shadow-sm"
        }`}
        // onMouseEnter={() => setIsHovered(true)}
        // onMouseLeave={() => setIsHovered(false)}
      >
        {/* Column Header */}
        <div
          className={`border-b ${finalBorderColor} ${finalBgColor} rounded-t-lg transition-colors duration-200 ${
            isEditing
              ? "p-3"
              : "h-[44px] min-h-[44px] max-h-[44px] box-border flex items-center justify-between"
          }`}
          style={!isEditing ? { padding: "6px 8px 6px 12px" } : undefined}
        >
          {isEditing ? (
            <div className="flex flex-col space-y-2 flex-1 w-full">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="text-sm font-semibold bg-white dark:bg-slate-700 border-2 border-blue-500 rounded px-2 py-1 outline-none flex-1 dark:text-white"
                  autoFocus
                />
                <button
                  onClick={handleRename}
                  className="p-1 rounded bg-green-500 text-white hover:bg-green-600 transition-colors"
                  title="Save changes"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setNewTitle(title);
                    setSelectedColor(color || "gray");
                    setIsEditing(false);
                  }}
                  className="p-1 rounded-lg bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 items-center pb-1">
                {["blue", "green", "yellow", "red", "purple", "pink", "indigo", "orange", "gray"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSelectedColor(c)}
                    className={`w-5 h-5 rounded-full border-2 transition-all ${
                      selectedColor === c
                        ? "border-gray-900 dark:border-white scale-110 shadow-sm"
                        : "border-transparent hover:scale-105 opacity-70 hover:opacity-100"
                    }`}
                    style={{
                      backgroundColor:
                        c === "gray"
                          ? "#6b7280"
                          : c === "blue"
                          ? "#3b82f6"
                          : c === "green"
                          ? "#22c55e"
                          : c === "yellow"
                          ? "#eab308"
                          : c === "red"
                          ? "#ef4444"
                          : c === "purple"
                          ? "#a855f7"
                          : c === "pink"
                          ? "#ec4899"
                          : c === "indigo"
                          ? "#6366f1"
                          : c === "orange"
                          ? "#f97316"
                          : "#6b7280",
                    }}
                    title={c.charAt(0).toUpperCase() + c.slice(1)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              <div
                className="flex items-center gap-2 flex-1 min-w-0 h-[20px] box-border"
                style={{ padding: "0 0 0 2px" }}
              >
                <h3
                  className={`text-sm font-semibold ${finalTextColor} uppercase tracking-wide truncate leading-[20px] select-none ${
                    status !== "archive"
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  }`}
                  onClick={() => {
                    if (status !== "archive") {
                      setNewTitle(title);
                      setSelectedColor(color || "gray");
                      setIsEditing(true);
                    }
                  }}
                  title={
                    status !== "archive"
                      ? "Click to rename column"
                      : "Archive column cannot be renamed"
                  }
                >
                  {title}
                </h3>

                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${finalTextColor} bg-white/50 dark:bg-slate-700/50 shadow-sm transition-colors duration-200 flex-shrink-0 leading-none h-[20px] flex items-center justify-center`}
                >
                  {cards.length}
                </span>

                {description && !isEmptyHtml(description) && (
                  <div className="relative flex-shrink-0 flex items-center">
                    <button
                      ref={descriptionIconRef}
                      onMouseEnter={() => setShowDescriptionTooltip(true)}
                      onMouseLeave={() => setShowDescriptionTooltip(false)}
                      className="p-0.5 rounded-full hover:bg-black/5 dark:hover:bg-white/5 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>

                    {showDescriptionTooltip && (
                      <div className="absolute left-0 top-full mt-2 w-64 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl z-[100] text-sm text-gray-700 dark:text-slate-200 normal-case tracking-normal font-normal">
                        <div className="font-semibold mb-1 border-b pb-1 text-gray-900 dark:text-white border-gray-200 dark:border-slate-700">
                          Column Description
                        </div>
                        <div
                          className="max-h-48 overflow-y-auto ql-editor p-0 text-xs"
                          dangerouslySetInnerHTML={{ __html: description }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="relative flex-shrink-0 flex items-center" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className={`w-6 h-6 flex items-center justify-center rounded relative transition-colors duration-200 ${
                    sortBy
                      ? "text-blue-600 dark:text-blue-400 bg-blue-50/80 dark:bg-blue-900/30"
                      : "hover:bg-black/5 dark:hover:bg-white/5 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
                  }`}
                  title={
                    sortBy
                      ? `Column actions (Sorted by ${
                          sortBy === "recentlyMoved"
                            ? "Recently Moved"
                            : sortBy === "recentlyCreated"
                            ? "Recently Created"
                            : "Card Number"
                        })`
                      : "Column actions"
                  }
                >
                  <MoreVertical className="w-4 h-4" />
                  {sortBy && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                  )}
                </button>

                {showMenu && (
                  <div className="absolute right-0 top-8 w-56 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 py-1.5 z-20">
                    {status !== "archive" && (
                      <>
                        <button
                          onClick={() => {
                            setNewTitle(title);
                            setSelectedColor(color || "gray");
                            setIsEditing(true);
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center space-x-2"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span>Rename Column</span>
                        </button>
                        <button
                          onClick={() => {
                            setIsEditingDescription(true);
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center space-x-2"
                        >
                          <FileText className="w-4 h-4" />
                          <span>Edit Description</span>
                        </button>
                        {/* Only show delete button if column has no cards */}
                        {cards.length === 0 && (
                          <>
                            <div className="border-t border-gray-100 dark:border-slate-700 my-1"></div>
                            <button
                              onClick={handleDelete}
                              className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete Column</span>
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => {
                            onAddCard(status);
                            setShowMenu(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center space-x-2"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Add Card</span>
                        </button>
                        {cards.length > 0 && onMoveAllCards && (
                          <>
                            <div className="border-t border-gray-100 dark:border-slate-700 my-1"></div>
                            <button
                              onClick={() => {
                                onMoveAllCards(status);
                                setShowMenu(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center space-x-2"
                            >
                              <ArrowRight className="w-4 h-4" />
                              <span>Move all cards</span>
                            </button>
                          </>
                        )}
                        <div className="border-t border-gray-100 dark:border-slate-700 my-1"></div>
                      </>
                    )}

                    <div className="px-3 py-1 text-[11px] font-semibold text-gray-400 dark:text-slate-400 uppercase tracking-wider">
                      Sort Cards
                    </div>
                    <button
                      onClick={() => handleSort("recentlyMoved")}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                        sortBy === "recentlyMoved"
                          ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50/60 dark:bg-blue-900/20"
                          : "text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <ArrowLeftRight className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                        <span>Sort by Recently Moved</span>
                      </div>
                      {sortBy === "recentlyMoved" && (
                        <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleSort("recentlyCreated")}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                        sortBy === "recentlyCreated"
                          ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50/60 dark:bg-blue-900/20"
                          : "text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                        <span>Sort by Recently Created</span>
                      </div>
                      {sortBy === "recentlyCreated" && (
                        <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                    <button
                      onClick={() => handleSort("cardNumber")}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                        sortBy === "cardNumber"
                          ? "text-blue-600 dark:text-blue-400 font-medium bg-blue-50/60 dark:bg-blue-900/20"
                          : "text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Hash className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                        <span>Sort by Card Number</span>
                      </div>
                      {sortBy === "cardNumber" && (
                        <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      )}
                    </button>
                    {sortBy && (
                      <button
                        onClick={() => handleSort(null)}
                        className="w-full text-left px-3 py-1.5 text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex items-center space-x-2 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        <span>Reset to default order</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Cards List */}
        <ColumnDroppable status={status}>
          {!hasBeenVisible ? (
            // Placeholder until the column scrolls near the viewport — keeps the
            // column shell (and drop target) without mounting its cards.
            <div
              className="p-3 flex-1 min-h-0 overflow-hidden"
              aria-hidden="true"
            >
              <div className="text-center py-8 text-xs text-gray-400 dark:text-slate-500">
                Loading…
              </div>
            </div>
          ) : (
          <SortableContext
            items={visibleCards.map((card) => card._id || card.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              ref={cardsScrollRef}
              onScroll={(event) => {
                const el = event.currentTarget;
                cardsScrollTopRef.current = el.scrollTop;
                // Load more when scrolled near the bottom.
                if (
                  cards.length > visibleCount &&
                  el.scrollHeight - el.scrollTop - el.clientHeight < 300
                ) {
                  setVisibleCount((c) =>
                    Math.min(c + VISIBLE_STEP, cards.length)
                  );
                }
              }}
              style={{ overflowAnchor: "none" }}
              className="p-1 space-y-1 flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-slate-600 scrollbar-track-white dark:scrollbar-track-slate-900"
            >
              {visibleCards.map((card) => (
                <CardItem
                  key={card._id || card.id}
                  card={card}
                  onCardUpdated={onCardUpdated}
                  onCardDeleted={onCardDeleted}
                  onCardRestored={onCardRestored}
                  onStatusChange={onStatusChange}
                  onCardClick={onCardClick}
                  activeTimer={activeTimer}
                  activeTimers={activeTimers}
                  onTimerStart={onTimerStart}
                  onTimerStop={onTimerStop}
                  projectId={projectId}
                />
              ))}

              {/* Load-more affordance for very large columns */}
              {cards.length > visibleCount && (
                <button
                  type="button"
                  onClick={() =>
                    setVisibleCount((c) => Math.min(c + VISIBLE_STEP, cards.length))
                  }
                  className="w-full py-2 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 transition-colors"
                >
                  Show more ({cards.length - visibleCount} hidden)
                </button>
              )}

              {/* Empty state for when no cards */}
              {cards.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-gray-100 dark:bg-slate-800 flex items-center justify-center transition-colors duration-200">
                    <span className="text-base font-semibold">📋</span>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-slate-500">No cards yet</p>
                </div>
              )}
            </div>
          </SortableContext>
          )}
        </ColumnDroppable>

        {/* Add Card Button - Always Visible (except for Archive column) */}
        {status !== "archive" && (
          <div className=" border-gray-100 dark:border-slate-700">
            <button
              onClick={() => onAddCard(status)}
              className="w-full flex items-center justify-center space-x-1 py-2 px-2 text-sm text-white hover:text-white rounded-bl-lg rounded-br-lg   bg-blue-600 font-base align-middle transition-all duration-300 hover:bg-blue-700 "
            >
              <Plus className="w-4 h-4" />
              <span>Add a card</span>
            </button>
          </div>
        )}

        {/* Delete Column Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          title="Delete Column"
          message={`Are you sure you want to delete the "${title}" column? All cards in this column will be moved to "To Do".`}
          confirmText="Delete Column"
          cancelText="Cancel"
          type="danger"
          isLoading={false}
        />

        {/* Edit Description Modal */}
        {isEditingDescription && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div
              ref={descriptionModalRef}
              className="bg-white dark:bg-slate-800 rounded-lg p-6 w-[35rem] max-w-lg mx-auto shadow-2xl transition-colors duration-200"
            >
              <h3 className="text-base font-semibold mb-4 flex items-center dark:text-white">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                Edit Column Description: {title}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Description
                  </label>
                  <div className="border border-gray-300 dark:border-slate-700 rounded-lg overflow-hidden">
                    <SimpleQuillEditor
                      value={editedDescription}
                      onChange={setEditedDescription}
                      placeholder="Enter column description"
                      height="200px"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setIsEditingDescription(false)}
                  className="px-4 py-2 text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateDescription}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Compare primitive props
    if (
      prevProps.title !== nextProps.title ||
      prevProps.description !== nextProps.description ||
      prevProps.status !== nextProps.status ||
      prevProps.color !== nextProps.color ||
      prevProps.projectId !== nextProps.projectId
    ) {
      return false;
    }

    // Compare cards array - check if same cards in same order
    if (prevProps.cards.length !== nextProps.cards.length) {
      return false;
    }

    // Compare each card by ID and every field CardItem's own comparator treats
    // as render-relevant (kept in sync with CardItem.js's memo comparator, so
    // this outer memo never silently swallows an update before it reaches
    // CardItem — e.g. isComplete/readBy changes were previously missed here).
    for (let i = 0; i < prevProps.cards.length; i++) {
      const prevCard = prevProps.cards[i];
      const nextCard = nextProps.cards[i];
      if (
        prevCard._id !== nextCard._id ||
        prevCard.order !== nextCard.order ||
        prevCard.title !== nextCard.title ||
        prevCard.status !== nextCard.status ||
        prevCard.isArchived !== nextCard.isArchived ||
        prevCard.isComplete !== nextCard.isComplete ||
        prevCard.completedAt !== nextCard.completedAt ||
        prevCard.completedBy !== nextCard.completedBy ||
        prevCard.priority !== nextCard.priority ||
        prevCard.dueDate !== nextCard.dueDate ||
        prevCard.readBy?.length !== nextCard.readBy?.length ||
        (prevCard.labels || []).map(l => String(l?._id || l?.id || l?.name || l || "")).sort().join(",") !==
          (nextCard.labels || []).map(l => String(l?._id || l?.id || l?.name || l || "")).sort().join(",") ||
        (prevCard.assignees || []).map(a => String(a?._id || a?.id || a || "")).sort().join(",") !==
          (nextCard.assignees || []).map(a => String(a?._id || a?.id || a || "")).sort().join(",") ||
        prevCard.attachments?.length !== nextCard.attachments?.length ||
        (prevCard.commentCount ?? 0) !==
          (nextCard.commentCount ?? 0) ||
        prevCard.hasUnseenComments !== nextCard.hasUnseenComments
      ) {
        return false;
      }
    }

    // Props are equal, don't re-render
    return true;
  }
);

export default ListColumn;
